import React from 'react';
import { Type, Image, Minus, Grid, Lock, Pen } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface TemplateEditorSidebarProps {
  onAddElement: (type: 'text' | 'image' | 'divider' | 'signature') => void;
  showGrid: boolean;
  onShowGridChange: (checked: boolean) => void;
  preventOverlap: boolean;
  onPreventOverlapChange: (checked: boolean) => void;
  isAdmin?: boolean;
  isCorporateDefault?: boolean;
  onCorporateDefaultChange?: (checked: boolean) => void;
  isLocked?: boolean;
  onLockedChange?: (checked: boolean) => void;
}

export const TemplateEditorSidebar: React.FC<TemplateEditorSidebarProps> = ({
  onAddElement,
  showGrid,
  onShowGridChange,
  preventOverlap,
  onPreventOverlapChange,
  isAdmin,
  isCorporateDefault,
  onCorporateDefaultChange,
  isLocked,
  onLockedChange
}) => {
  return (
    <TooltipProvider delayDuration={300}>
      <div className="w-14 bg-white border-r flex flex-col items-center py-4 gap-4">
        {/* Add Elements Section */}
        <div className="flex flex-col items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => onAddElement('text')}
                className="w-10 h-10 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                aria-label="Add Text"
              >
                <Type className="w-5 h-5 text-gray-700" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Add Text</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => onAddElement('image')}
                className="w-10 h-10 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                aria-label="Add Image"
              >
                <Image className="w-5 h-5 text-gray-700" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Add Image</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => onAddElement('divider')}
                className="w-10 h-10 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                aria-label="Add Divider"
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Add Divider</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => onAddElement('signature')}
                className="w-10 h-10 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                aria-label="Add Signature"
              >
                <Pen className="w-5 h-5 text-gray-700" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Add Signature (LO-E-Sign)</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator className="w-8" />

        {/* Canvas Options Section */}
        <div className="flex flex-col items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center">
                <Checkbox
                  id="sidebar-grid"
                  checked={showGrid}
                  onCheckedChange={(checked) => onShowGridChange(checked as boolean)}
                  className="data-[state=checked]:bg-gray-700"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className="flex items-center gap-2">
                <Grid className="w-4 h-4" />
                <span>Show Grid</span>
              </div>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center">
                <Checkbox
                  id="sidebar-overlap"
                  checked={preventOverlap}
                  onCheckedChange={(checked) => onPreventOverlapChange(checked as boolean)}
                  className="data-[state=checked]:bg-gray-700"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                <span>Prevent Overlap</span>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Corporate Controls - Only for Admins */}
        {isAdmin && (
          <>
            <Separator className="w-8" />
            <div className="flex flex-col items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center">
                    <Checkbox
                      id="sidebar-corporate"
                      checked={isCorporateDefault}
                      onCheckedChange={(checked) => onCorporateDefaultChange?.(checked as boolean)}
                      className="data-[state=checked]:bg-green-600"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Corporate Default</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center justify-center">
                    <Checkbox
                      id="sidebar-locked"
                      checked={isLocked}
                      onCheckedChange={(checked) => onLockedChange?.(checked as boolean)}
                      disabled={isCorporateDefault}
                      className="data-[state=checked]:bg-gray-700"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{isCorporateDefault ? 'Auto-locked (Corporate)' : 'Locked Template'}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};
